import { 
    TitleLarge,
    TitleMedium,
    BodyXLMedium,
    BodyRegular
} from '@/components/typography';
import { GenericCard } from '@/components/cards';
import { TextBadge } from '@/components/elements';
import { LazyVideo } from '@/components/elements';

// Ico structure images
import HeroImage from '@/assets/img/ui/27.png';
// First cards grid images
import IconHourly from '@/assets/img/ui/19.png';
import IconBot from '@/assets/img/ui/3.png';
import IconLeaderboard from '@/assets/img/ui/4.png';
// Second cards grid images
import IconNFT from '@/assets/img/ui/5.png';
import IconExpansion from '@/assets/img/ui/6.png';
// Anon image for the Staking section
import AnonImage from '@/assets/img/ui/Anon-1.png';
// Last section has an animation
import IntroAnim from '@/assets/anim/home-intro.webm';
// Last section icons
import IconConfirmTransaction from '@/assets/img/ui/16.png';
import IconEducation from '@/assets/img/ui/8.png';

export const Features = () => {
    return (
        <div className="w-full bg-background">
            {/* Hero Section */}
            <section className="w-full flex justify-center px-4 pb-4 pt-16 lg:pt-44 lg:pb-16 relative">
                {/* Glow effects */}
                <div className="absolute right-1/4 bottom-1/4 w-96 h-96 opacity-60 bg-cyan-400 rounded-full blur-[282.13px] pointer-events-none" />
                {/* Hero content itself */}
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12 relative z-10">
                    <div className="w-full lg:flex-1 flex flex-col items-start gap-4 lg:gap-6 lg:max-w-[716px]">
                        <TextBadge>Features</TextBadge>
                        <TitleLarge className="text-white">
                            One Ecosystem. <span className="text-primary">Infinite Value</span>.
                        </TitleLarge>
                    </div>

                    <div className="hidden lg:flex lg:flex-1 items-center justify-center relative">
                        <div className="absolute top-0 right-1/4 w-96 h-96 opacity-60 bg-primary rounded-full blur-[282.13px] pointer-events-none" />
                        <img 
                            className="absolute -top-60 right-0 min-w-[675.73px] rotate-[3.95deg]" 
                            src={HeroImage}
                            alt="Features Hero section image"
                        />
                    </div>
                </div>
            </section>

            {/* AI NFT Auctions Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-start gap-6 lg:gap-12">
                    <div className="flex flex-col gap-6">
                        <TitleMedium className="text-white">
                            AI NFT <span className="text-primary">Auctions</span>
                        </TitleMedium>
                        <BodyRegular className="text-white/80 lg:text-white/60 max-w-[320px] lg:max-w-[633px]">
                            Each week, 100 NFTs are generated through the Humour Intelligence System - where AI agents trained on comedians and philosophers debate live crypto events, producing unique, time-stamped satire pieces ranked by rarity (#1–#100).
                        </BodyRegular>
                    </div>

                    <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-6 mt-4 lg:mt-auto">
                        <GenericCard
                            image={IconHourly}
                            title="Live-streamed 24-hour auctions"
                            titleClassName="max-w-[160px] lg:max-w-none"
                        />
                        <GenericCard
                            image={IconBot}
                            title="Optional &quot;Auction Bot&quot; for global participation"
                            titleClassName="max-w-[224px] lg:max-w-none"
                        />
                        <GenericCard
                            image={IconLeaderboard}
                            title="Leaderboards & anti-sniping mechanisms"
                            titleClassName="max-w-[224px] lg:max-w-none"
                        />
                    </div>
                </div>
            </section>

            {/* Staking & Governance Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col lg:flex-row items-start gap-6 lg:gap-16">
                    <div className="w-full lg:w-[572px] flex flex-col gap-4 flex-shrink-0 relative order-2 lg:order-1">
                        <img 
                            src={AnonImage}
                            className="hidden lg:block absolute right-0 top-1/3 -translate-y-1/2 w-96 h-96 rotate-[175.68deg] scale-y-[-1] opacity-50 pointer-events-none z-0" 
                            alt="Staking & Governance background image" 
                        />
                        
                        <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 w-[605px] h-[605px] bg-primary/20 rounded-full blur-[282.13px] pointer-events-none z-10" />
                        
                        <div className="flex flex-col gap-4 lg:pl-0 relative z-20">
                            <div className="w-full lg:w-[332px] p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col gap-1">
                                <BodyXLMedium className="text-primary">+5%</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">for holding NFTs from last 3 auctions</BodyRegular>
                            </div>
                            <div className="w-full lg:w-[332px] p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col gap-1 lg:ml-20">
                                <BodyXLMedium className="text-primary">+2%</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">every 6 months re-lock (capped at 36m)</BodyRegular>
                            </div>
                            <div className="w-full lg:w-[332px] p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col gap-1 lg:ml-40">
                                <BodyXLMedium className="text-primary">Tier 1 and Tier 2</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">governance with wallet caps</BodyRegular>
                            </div>
                        </div>
                    </div>

                    <div className="w-full lg:flex-1 flex flex-col gap-4 lg:gap-6 lg:pt-16 order-1 lg:order-2">
                        <TitleMedium className="text-white">
                            <span className="text-primary">Staking</span> & Governance
                        </TitleMedium>
                        <BodyRegular className="text-white/80 lg:text-white/60 max-w-[320px] lg:max-w-[492px]">
                            Stake for 12, 24, or 36 months to earn up to 50% APY plus loyalty and participation bonuses. Long-term stakers gain governance privileges, voting rights, and recognition within on-chain dashboards.
                        </BodyRegular>
                    </div>
                </div>
            </section>

            {/* Marketplace Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center gap-8 lg:gap-12">
                    <div className="flex flex-col items-center gap-4">
                        <TitleMedium className="text-white text-center">Marketplace</TitleMedium>
                        <BodyRegular className="text-white/80 lg:text-white/60 text-center max-w-[320px] lg:max-w-[492px]">
                            The CCRAP Marketplace allows holders to trade and merge NFTs under the 110% floor rule. Profit Split: 70% to seller, 30% to treasury.
                        </BodyRegular>
                    </div>

                    <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-11 lg:gap-6">
                        <GenericCard
                            image={IconHourly}
                            title="Countdown previews"
                            description="for upcoming drops"
                            titleClassName="max-w-none"
                        />
                        <GenericCard
                            image={IconNFT}
                            title="NFT Fusion"
                            description="for exclusive perks and supply reduction"
                            titleClassName="max-w-none"
                        />
                        <GenericCard
                            image={IconExpansion}
                            title="Future expansion"
                            description="for external creators"
                            titleClassName="max-w-none"
                        />
                    </div>
                </div>
            </section>

            {/* Education & Social Impact + Transparency Section */}
            <section className="w-full flex justify-center px-4 py-16 relative">
                <div className='max-w-[320px] lg:max-w-[1200px] lg:pt-24 lg:pb-36 w-full relative'>
                    {/* Image layer - absolute, behind everything */}
                    <div className="absolute top-0 left-0 h-full w-full inset-0 pointer-events-none z-0">
                        <img 
                            className="absolute -right-24 lg:right-auto lg:left-0 bottom-10 lg:-bottom-24 w-[200px] lg:w-[465.30px]" 
                            src={IconEducation}
                            alt="Education & Social Impact" 
                        />
                        
                        <img 
                            className="absolute right-[5%] top-0 w-96 rotate-[-6.08deg] hidden lg:block " 
                            src={IconConfirmTransaction} 
                            alt="Transparency & Audits" 
                        />
                        
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1496px] h-[842px] hidden lg:block ">
                            <LazyVideo
                                loop={false}
                                src={IntroAnim}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>

                    <div className="w-full max-w-[320px] lg:max-w-[1200px] relative z-10 flex flex-col gap-8 lg:gap-12">
                        <div className="flex flex-col gap-4 lg:gap-6 lg:max-w-md">
                            <TitleMedium className="text-white">
                                <span className="text-primary">Education</span> & Social Impact
                            </TitleMedium>
                            <BodyRegular className="text-white/80 lg:text-white/60">
                                Proceeds above the burn threshold feed the Education workshops, and fintech programs. Governance participants oversee fund allocation, ensuring transparency and measurable social value.
                            </BodyRegular>
                        </div>
                        
                        <div className="flex flex-col gap-4 lg:gap-6 lg:max-w-md lg:ml-auto">
                            <TitleMedium className="text-white">
                                <span className="text-primary">Transparency</span> & Audits
                            </TitleMedium>
                            <BodyRegular className="text-white/80 lg:text-white/60">
                                All CCRAP contracts undergo independent audits before launch. Treasury wallets and reward pools are public, and all governance proposals are logged on-chain for accountability.
                            </BodyRegular>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};